import * as React from 'react';
import Text, { TextColor } from 'components/Text';
import { Intent } from 'translations/types';
import 'translations/components/styles/TranslationPreview.scss';

export type Props = {
  messageText: string;
  activeIntent: Intent;
  forceFullTextDisplay: boolean;
  toggleTextExpanded: (expandText: boolean) => void;
};

export default function TranslationPreviewOriginalText({
  messageText,
  activeIntent,
  forceFullTextDisplay,
  toggleTextExpanded,
}: Props) {
  const textColor: TextColor = activeIntent === 'Off' ? 'inherit' : 'graphite';

  const textElementRef = React.useRef<HTMLDivElement>() as React.MutableRefObject<HTMLDivElement>;
  const [textOverflow, setTextOverflow] = React.useState(false);
  const updateOverflow = () => setTextOverflow(messageOverflowsWithoutWrap(textElementRef));

  React.useEffect(() => {
    updateOverflow();
    window.addEventListener('resize', updateOverflow);
    return () => window.removeEventListener('resize', updateOverflow);
  });
  return (
    <div className="TranslationPreview-original-text-box">
      <Text align="left" size="xsmall" weight="semibold" color={textColor}>
        Original
      </Text>
      <ReferenceElement messageText={messageText} textElementRef={textElementRef} />
      {textOverflow && !forceFullTextDisplay ? (
        <TruncatedMessageTextArea
          messageText={messageText}
          textColor={textColor}
          toggleTextExpanded={toggleTextExpanded}
        />
      ) : (
        <ExpandedMessageTextArea
          messageText={messageText}
          textColor={textColor}
          forcingFullTextDisplay={forceFullTextDisplay}
          toggleTextExpanded={toggleTextExpanded}
        />
      )}
    </div>
  );
}

// This component is *not displayed*
// It is used to determine whether the original message is long enough
//   that it needs to be truncated with a "Show More" link
function ReferenceElement({
  messageText,
  textElementRef,
}: {
  messageText: string;
  textElementRef: React.MutableRefObject<HTMLDivElement>;
}) {
  return (
    <div className="TranslationPreview-reference-element-do-not-display">
      <div ref={textElementRef}>
        <Text align="left" size="small" wrap={false}>
          [reference] {messageText}
        </Text>
      </div>
    </div>
  );
}

function TruncatedMessageTextArea({
  messageText,
  textColor,
  toggleTextExpanded,
}: {
  messageText: string;
  textColor: TextColor;
  toggleTextExpanded: (expandText: boolean) => void;
}) {
  return (
    <div className="TranslationPreview-truncated-message-text-area">
      <div className="TranslationPreview-truncated-text-message-wrapper">
        <Text
          align="left"
          size="small"
          color={textColor}
          wrap={false}
          className="TranslationPreview-truncated-message-text">
          {messageText}
        </Text>
      </div>
      <div>
        <SeeMoreText toggleTextExpanded={toggleTextExpanded} />
      </div>
    </div>
  );
}

function ExpandedMessageTextArea({
  messageText,
  textColor,
  forcingFullTextDisplay,
  toggleTextExpanded,
}: {
  messageText: string;
  textColor: TextColor;
  forcingFullTextDisplay: boolean;
  toggleTextExpanded: (expandText: boolean) => void;
}) {
  return (
    <div>
      <Text
        align="left"
        size="small"
        color={textColor}
        wrap={true}
        className="expanded-message-text">
        {messageText}
      </Text>
      {forcingFullTextDisplay && <SeeLessText toggleTextExpanded={toggleTextExpanded} />}
    </div>
  );
}

function SeeMoreText({
  toggleTextExpanded,
}: {
  toggleTextExpanded: (expandText: boolean) => void;
}) {
  return (
    <Text
      className="TranslationPreview-expand-original-toggle"
      align="right"
      weight="semibold"
      size="small"
      color="graphite"
      onClick={() => toggleTextExpanded(true)}
      wrap={false}>
      See more
    </Text>
  );
}

function SeeLessText({
  toggleTextExpanded,
}: {
  toggleTextExpanded: (expandText: boolean) => void;
}) {
  return (
    <Text
      className="TranslationPreview-truncate-original-toggle"
      align="left"
      weight="semibold"
      size="small"
      color="mascot"
      onClick={() => toggleTextExpanded(false)}>
      {'See less'}
    </Text>
  );
}

function messageOverflowsWithoutWrap(elementRef: React.MutableRefObject<HTMLDivElement>) {
  if (!elementRef) {
    return false;
  }
  return elementRef.current.offsetWidth < elementRef.current.scrollWidth;
}
